/**
 * SharedDependencyAlert - Foundation Task Callout
 * Displays information about tasks that will be unlocked when a foundation task is completed
 */

import React from 'react';
import { Unlock, ChevronRight } from 'lucide-react';
import { getUnlockedTasks } from '../../utils/taskUtils';

const SharedDependencyAlert = ({
  foundationTask,
  allTasks = [],
  onTaskClick,
  className = ''
}) => {
  const unlockedTasks = getUnlockedTasks(foundationTask, allTasks);
  const unlockCount = foundationTask.unlock_count || unlockedTasks.length;
  const unlockDescription = foundationTask.unlock_description;

  if (unlockCount === 0 || foundationTask.status === 'completed') {
    return null;
  }

  return (
    <div className={`bg-blue-50 border-2 border-blue-200 rounded-lg p-4 ${className}`}>
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className="flex-shrink-0 w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
          <Unlock className="w-5 h-5 text-white" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header */}
          <h4 className="text-sm font-semibold text-blue-900 mb-1">
            Foundation Task - Unlocks Progress
          </h4>

          {/* Description */}
          <p className="text-sm text-blue-800 mb-3">
            {unlockDescription || `Complete this task to unlock ${unlockCount} additional task${unlockCount !== 1 ? 's' : ''}`}
          </p>

          {/* List of Unlocked Tasks */}
          {unlockedTasks.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-blue-700 uppercase tracking-wide">
                Will Unlock:
              </p>
              <div className="space-y-1">
                {unlockedTasks.slice(0, 5).map((task) => (
                  <button
                    key={task.id}
                    onClick={() => onTaskClick && onTaskClick(task)}
                    className="w-full flex items-center gap-2 px-3 py-2 bg-white border border-blue-200 rounded text-sm text-blue-900 hover:bg-blue-100 hover:border-blue-300 transition-colors text-left group"
                  >
                    <ChevronRight className="w-4 h-4 text-blue-500 flex-shrink-0 group-hover:translate-x-0.5 transition-transform" />
                    <span className="flex-1 truncate">{task.title}</span>
                    {task.category && (
                      <span className="flex-shrink-0 text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">
                        {task.category}
                      </span>
                    )}
                  </button>
                ))}
                {unlockedTasks.length > 5 && (
                  <p className="text-xs text-blue-600 pl-3 pt-1">
                    + {unlockedTasks.length - 5} more task{unlockedTasks.length - 5 !== 1 ? 's' : ''}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* If we don't have the task details, just show the count */}
          {unlockedTasks.length === 0 && unlockCount > 0 && (
            <p className="text-sm text-blue-700 font-medium">
              {unlockCount} task{unlockCount !== 1 ? 's' : ''} waiting to be unlocked
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default SharedDependencyAlert;
